import { useCallback, useRef, useState } from "react";
import { FiMinus, FiPlus } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from "@fortawesome/free-solid-svg-icons";

// ─── Quantity Modal ───────────────────────────────────────────────────────────
export const QuantityModal = ({ item, onClose, onConfirm }) => {
    const max = item.Quantity;
    const [qty, setQty] = useState(1);
    const [error, setError] = useState("");

    const validate = (val) => {
        const n = parseInt(val, 10);
        if (isNaN(n) || n < 1) { setError("Enter at least 1"); return false; }
        if (n > max) { setError(`Max allowed is ${max}`); return false; }
        setError("");
        return true;
    };

    const handleChange = (val) => {
        setQty(val);
        validate(val);
    };

    const adjust = (delta) => {
        const next = Math.max(1, Math.min(max, (parseInt(qty, 10) || 0) + delta));
        handleChange(next);
    };

    const handleConfirm = () => {
        if (validate(qty)) onConfirm(parseInt(qty, 10));
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-xl shadow-xl w-80 overflow-hidden">
                {/* Header */}
                <div className="bg-violet-700 text-white px-4 py-3 flex justify-between items-center">
                    <div>
                        <div className="font-semibold text-sm leading-snug tracking-wider">{item.ItemName}</div>
                        <div className="text-xs opacity-90 tracking-wide">Max qty: <strong>{max}</strong></div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-md bg-white/20 hover:bg-white/30 flex items-center justify-center text-white text-lg leading-none transition-colors"
                        aria-label="Close"
                    >
                        <IoClose className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-4 py-3">
                    <p className="text-sm text-gray-600 mb-2 font-semibold">How many to mark as done?</p>
                    <div className="flex items-center gap-1.5 mb-1">
                        <button
                            onClick={() => adjust(-1)}
                            className="p-1.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all"
                        >
                            <FiMinus size={14} />
                        </button>

                        <input
                            type="number"
                            value={qty}
                            min={1}
                            max={max}
                            onChange={(e) => handleChange(e.target.value)}
                            className={`w-20 text-center text-sm font-semibold rounded-md border py-1 outline-none transition-colors
                                ${error
                                    ? "border-red-400 bg-red-50 text-red-700"
                                    : "border-gray-200 bg-white text-gray-900 focus:border-violet-400"
                                }`}
                        />

                        <button
                            onClick={() => adjust(1)}
                            className="p-1.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all"
                        >
                            <FiPlus size={14} />
                        </button>
                    </div>

                    {error && (
                        <div className="text-xs font-semibold italic tracking-wide text-red-500 mt-1.5 mb-0.5">{error}</div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 pb-4 flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 active:scale-95 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!!error || qty === ""}
                        className="flex-[2] py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-all"
                    >
                        <FontAwesomeIcon icon={faCheck} className="text-sm" />
                        Mark done
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Item Row ─────────────────────────────────────────────────────────────────
export const ItemRow = ({ item, onOpenModal, onLongPress }) => {
    const pressTimer = useRef(null);
    const didLongPress = useRef(false);
    const LONG_PRESS_MS = 500;

    const startPress = useCallback(() => {
        didLongPress.current = false;
        pressTimer.current = setTimeout(() => {
            didLongPress.current = true;
            navigator.vibrate?.(60);
            onLongPress();
        }, LONG_PRESS_MS);
    }, [onLongPress]);

    const cancelPress = useCallback(() => {
        clearTimeout(pressTimer.current);
    }, []);

    const handleClick = useCallback(() => {
        if (!didLongPress.current) onOpenModal();
    }, [onOpenModal]);

    return (
        <div className="grid grid-cols-[36px_1fr_72px] items-center px-2 py-2 font-medium text-gray-800 text-[10px] xl:text-xs">
            {/* Quantity */}
            <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold">
                {item.Quantity}
            </div>

            {/* Item Name + Modifications */}
            <div className="break-words leading-snug px-0.5 xl:!px-1">
                <div>{item.ItemName}</div>
                {item.Modifications && item.Modifications.trim() !== "" && (
                    <div className="text-[10px] xl:text-xs font-normal italic text-violet-600 mt-0.5 break-words tracking-wide">
                        {item.Modifications}
                    </div>
                )}
            </div>

            {/* Check Button */}
            <div className="flex gap-1 xl:gap-2 justify-end">
                <button
                    onPointerDown={startPress}
                    onPointerUp={cancelPress}
                    onPointerLeave={cancelPress}
                    onClick={handleClick}
                    className="w-6 xl:w-7 h-6 xl:h-7 rounded-md bg-green-100 text-green-700 hover:bg-green-600 hover:text-white active:scale-90 active:bg-green-700 transition-all duration-150 flex items-center justify-center select-none"
                    aria-label="Mark item done"
                >
                    <FontAwesomeIcon icon={faCheck} className="text-[10px] xl:text-xs" />
                </button>
            </div>
        </div>
    );
};